import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';

/**
 * VideoPage — verca.io/video/:id (new) and verca.io/video/:folder/:file (older)
 *
 * Plays a video from Verca's public `videos` storage bucket, for links sent by
 * text (e.g. verca.io/video/9db7a907-0fbc-4d3e-835e-8041f394d165/1793492646467-p0n39v).
 * Same look as the Demo page: white page, the Verca wordmark in a blurred
 * header that tucks away on scroll, a big title and caption, and the App Store
 * / Google Play buttons at the bottom.
 *
 * The link carries only the storage path (uploader's folder + file name,
 * without ".mp4"); the full Supabase address is rebuilt here. Both parts are
 * checked against a strict pattern, so this page can only ever play files
 * from Verca's own bucket — never an arbitrary URL.
 */

const SUPABASE_VIDEOS_BASE =
  'https://pkkueomludagpuryqboq.supabase.co/storage/v1/object/public/videos';

// Uploader folder: a UUID (the user's id).
const FOLDER_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// File name without extension: letters, digits, dash, underscore only.
const FILE_PATTERN = /^[A-Za-z0-9_-]{1,80}$/;
// New short links (verca.io/video/k2plumX9aQ): exactly 10 letters and digits.
const SHORT_ID_PATTERN = /^[A-Za-z0-9]{10}$/;

// Same store links as the Demo page.
const APP_STORE_URL = 'https://apps.apple.com/us/app/verca/id6751428364';
const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.arthurhakobyandev.StreamChatDev';

// How far down the page you have to be before the header will hide.
const NAV_HIDE_THRESHOLD = 8;

const VideoPage = () => {
  // Two link shapes share this page:
  //   /video/:id            → videos/<id>.mp4        (new, short)
  //   /video/:folder/:file  → videos/<folder>/<file>.mp4 (older uploads)
  const { id, folder, file } = useParams();
  const navigate = useNavigate();
  const [failed, setFailed] = useState(false);
  const [navHidden, setNavHidden] = useState(false);
  const lastScrollY = useRef(0);

  // Null when the link doesn't look like one of ours. A trailing ".mp4" is
  // accepted too, in case a link was built from the full file name.
  const videoUrl = useMemo(() => {
    if (id != null) {
      const shortId = id.replace(/\.mp4$/i, '');
      return SHORT_ID_PATTERN.test(shortId) ? `${SUPABASE_VIDEOS_BASE}/${shortId}.mp4` : null;
    }
    const name = (file || '').replace(/\.mp4$/i, '');
    if (!FOLDER_PATTERN.test(folder || '') || !FILE_PATTERN.test(name)) return null;
    return `${SUPABASE_VIDEOS_BASE}/${folder}/${name}.mp4`;
  }, [id, folder, file]);

  const unavailable = !videoUrl || failed;

  // Header slides away on scroll down and back on scroll up — same as Demo,
  // but only once you've actually scrolled. On load the browser fires scroll
  // events of its own (restoring the last position, the video's size arriving
  // and shifting the layout, the URL bar settling), which used to hide the
  // header straight away.
  useEffect(() => {
    // Always open at the top, not wherever the previous page was scrolled to.
    window.scrollTo(0, 0);
    lastScrollY.current = 0;

    let userScrolled = false;
    const markUserScroll = () => {
      userScrolled = true;
    };

    const handleScroll = () => {
      const y = window.scrollY;
      const delta = y - lastScrollY.current;
      lastScrollY.current = y;
      if (y < 0) return; // iOS rubber-band overscroll
      if (y <= 0) {
        setNavHidden(false);
        return;
      }
      if (!userScrolled) return; // the browser moved, not the person
      if (delta > 0 && y > NAV_HIDE_THRESHOLD) setNavHidden(true);
      else if (delta < 0) setNavHidden(false);
    };

    window.addEventListener('touchmove', markUserScroll, { passive: true });
    window.addEventListener('wheel', markUserScroll, { passive: true });
    window.addEventListener('keydown', markUserScroll);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('touchmove', markUserScroll);
      window.removeEventListener('wheel', markUserScroll);
      window.removeEventListener('keydown', markUserScroll);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className="vp-root">
      <Helmet>
        <title>Video · Verca</title>
        <meta name="robots" content="noindex" />
        <meta name="theme-color" content="#ffffff" />
        {/* Opts out of browsers' automatic dark mode (Samsung Internet,
            Chrome's "dark mode for web contents"), which dims light pages. */}
        <meta name="color-scheme" content="light only" />
      </Helmet>

      {/* Fixed header — same treatment as the Demo page. */}
      <nav className={`vp-nav ${navHidden ? 'is-hidden' : ''}`}>
        <div className="vp-nav-inner">
          <button type="button" className="vp-logo" onClick={() => navigate('/')}>
            <span className="vp-logo-text">Verca</span>
          </button>
        </div>
      </nav>

      <div className="vp-content">
        {unavailable ? (
          <>
            <h1 className="vp-title">Video unavailable</h1>
            <p className="vp-caption">
              This video may have been removed, or the link is incomplete.
            </p>
            <div className="vp-unavailable">
              <i className="fas fa-video-slash" aria-hidden="true" />
            </div>
          </>
        ) : (
          <div className="vp-player">
            {/* "#t=0.1" makes iOS Safari show the first frame before playing,
                instead of a blank box. playsInline keeps it on the page on
                iPhone (full screen is in the controls). The frame shrink-wraps
                the video, so there are no black bars around it. */}
            <video
              key={videoUrl}
              className="vp-video"
              src={`${videoUrl}#t=0.1`}
              controls
              playsInline
              preload="metadata"
              onError={() => setFailed(true)}
            />
          </div>
        )}

        {/* Store links — the page's action, styled like Demo's final slide. */}
        <div className="vp-store-section">
          <p className="vp-store-label">Verca - For Businesses &amp; Clients</p>
          <div className="vp-store-buttons">
            <a className="vp-store-btn" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
              <i className="fab fa-apple" />
              <span className="vp-store-btn-text">
                <span className="vp-store-btn-small">Download on the</span>
                <span className="vp-store-btn-large">App Store</span>
              </span>
            </a>

            <a className="vp-store-btn" href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
              <i className="fab fa-google-play" />
              <span className="vp-store-btn-text">
                <span className="vp-store-btn-small">Get it on</span>
                <span className="vp-store-btn-large">Google Play</span>
              </span>
            </a>
          </div>
        </div>
      </div>

      {/* Class names are prefixed "vp-" so they never clash with other pages'
          styles (these style tags are global). */}
      <style jsx>{`
        /* The page behind everything stays white (some browsers, or the site's
           global styles, can otherwise show a dark background around it).
           Removed again when you leave this page. */
        :root {
          color-scheme: light only;
        }

        html,
        body {
          background: #ffffff;
        }

        .vp-root {
          --text-primary: #000000;
          --text-tertiary: #767676;
          --hairline: #e6e6e6;
          --hairline-soft: #ededed;

          min-height: 100vh;
          width: 100%;
          display: flex;
          align-items: flex-start;
          justify-content: center;
          /* Clears the fixed header, same as Demo. */
          padding: 79px 16px 32px;
          box-sizing: border-box;
          background: #ffffff;
          font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI',
            Roboto, 'Helvetica Neue', Arial, sans-serif;
          color: var(--text-primary);
          -webkit-font-smoothing: antialiased;
          -webkit-tap-highlight-color: transparent;
        }

        .vp-root *,
        .vp-root *::before,
        .vp-root *::after {
          box-sizing: border-box;
        }

        .vp-nav {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 100;
          padding: 1.25rem 1.5rem;
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border-bottom: 1px solid var(--hairline-soft);
          transform: translateY(0);
          transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
          will-change: transform;
        }

        .vp-nav.is-hidden {
          transform: translateY(-101%);
        }

        .vp-nav-inner {
          max-width: 640px;
          margin: 0 auto;
          display: flex;
          align-items: center;
        }

        .vp-logo {
          display: flex;
          align-items: center;
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
          font-family: inherit;
          transition: opacity 0.2s ease;
        }

        .vp-logo:hover {
          opacity: 0.65;
        }

        .vp-logo:focus-visible {
          outline: 2px solid #000000;
          outline-offset: 4px;
          border-radius: 4px;
        }

        .vp-logo-text {
          font-size: 1.35rem;
          font-weight: 700;
          letter-spacing: -0.5px;
          line-height: 1;
          color: #000000;
        }

        .vp-content {
          width: 100%;
          max-width: 720px;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .vp-title {
          margin: 0 0 10px;
          font-size: clamp(24px, 7vw, 30px);
          font-weight: 800;
          letter-spacing: -0.8px;
          line-height: 1.15;
          text-align: center;
          color: #000000;
          animation: vp-rise 0.45s ease both;
        }

        .vp-caption {
          margin: 0 0 20px;
          font-size: 16px;
          line-height: 1.55;
          color: var(--text-tertiary);
          text-align: center;
          animation: vp-rise 0.45s ease both;
          animation-delay: 0.05s;
        }

        /* Rounded frame, like the phone mockups on Demo. It wraps the video
           exactly, so its corners are the video's corners. */
        .vp-player {
          max-width: 100%;
          border-radius: 26px;
          overflow: hidden;
          line-height: 0;
          animation: vp-rise 0.5s ease both;
          animation-delay: 0.05s;
        }

        /* As big as fits: the full width for wide clips, or the height left
           under the header and above the download buttons for tall ones —
           whichever runs out first. The aspect ratio is always kept. */
        .vp-video {
          display: block;
          width: auto;
          height: auto;
          max-width: 100%;
          max-height: calc(100vh - 250px);
          max-height: calc(100svh - 250px);
        }

        .vp-unavailable {
          width: 100%;
          height: 220px;
          border-radius: 26px;
          border: 1px solid var(--hairline);
          background: #f6f6f6;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #b4b4b4;
          font-size: 40px;
        }

        /* Extra bottom margin gives mobile browsers room to collapse their
           URL bar, same as Demo. */
        .vp-store-section {
          width: 100%;
          max-width: 380px;
          margin-top: 24px;
          margin-bottom: 20vh;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .vp-store-label {
          margin: 0 0 14px;
          font-size: 14px;
          font-weight: 600;
          color: var(--text-tertiary);
          text-align: center;
        }

        .vp-store-buttons {
          display: flex;
          gap: 12px;
          width: 100%;
          justify-content: center;
        }

        /* Black, like Demo's buttons on its final slide. */
        .vp-store-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          flex: 1;
          padding: 12px 16px;
          background: #000000;
          border: 1px solid #000000;
          border-radius: 14px;
          color: #ffffff;
          text-decoration: none;
          animation: vp-pop 0.5s cubic-bezier(0.34, 1.45, 0.64, 1) both;
          animation-delay: 0.15s;
          transition: background 0.3s cubic-bezier(0.4, 0, 0.2, 1),
            border-color 0.3s ease,
            transform 0.35s cubic-bezier(0.34, 1.45, 0.64, 1);
        }

        .vp-store-btn:nth-child(2) {
          animation-delay: 0.23s;
        }

        .vp-store-btn:hover {
          background: #1f1f1f;
          border-color: #1f1f1f;
          transform: scale(1.04);
        }

        .vp-store-btn:focus-visible {
          outline: 2px solid #000000;
          outline-offset: 3px;
        }

        .vp-store-btn i {
          font-size: 26px;
          flex-shrink: 0;
          color: currentColor;
        }

        .vp-store-btn-text {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }

        .vp-store-btn-small {
          font-size: 11px;
          font-weight: 500;
          opacity: 0.7;
          letter-spacing: 0.3px;
          line-height: 1.2;
          text-transform: uppercase;
        }

        .vp-store-btn-large {
          font-size: 17px;
          font-weight: 600;
          letter-spacing: 0.2px;
          line-height: 1.2;
        }

        @keyframes vp-rise {
          from {
            opacity: 0;
            transform: translateY(12px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes vp-pop {
          from {
            opacity: 0;
            transform: scale(0.86);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .vp-title,
          .vp-caption,
          .vp-player,
          .vp-store-btn,
          .vp-store-btn:hover {
            animation: none;
            transition: none;
            transform: none;
          }

          .vp-nav {
            transition: none;
          }
        }
      `}</style>

      <link
        rel="stylesheet"
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css"
      />
      <link
        href="https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800;0,9..40,900&display=swap"
        rel="stylesheet"
      />
    </div>
  );
};

export default VideoPage;